// `ver_fotos`: el agente MIRA, de a diez, las fotos que el usuario adjuntó en montón —
// ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez.
//
// Cuando alguien engancha 7 o más fotos, no viajan en el mensaje: serían decenas de
// imágenes en ESTE turno y en todos los siguientes del historial. Viaja una línea con
// cuántas son y sus `foto_id`, y el agente pide los tramos que necesita ver. El tramo lo
// elige él según lo que le pidieron —no un contador ciego—, que es la diferencia entre
// "mirá las 40" y "mirá las 10 que te hacen falta para decidir".
//
// Mismo mecanismo que `ver_productos`: el `tool_result` lleva bloques de texto e imagen y
// el modelo las ve en el mismo turno. Acá está sólo lo puro (qué tramo y qué bloques); la
// subida al storage vive en `subirFotosDelLote.ts`, porque mirar y materializar son la
// misma operación: para que el modelo VEA una foto tiene que estar en nuestro storage, y
// para usarla como referencia de una generación, también.

import type { BloqueContenido } from './verProductos';

/** Cuántas fotos muestra UNA llamada. Cada una son tokens de imagen que después se
 *  arrastran en todo el historial: por eso se mira de a poco y a pedido. */
export const MAX_FOTOS_A_VER = 10;

export interface VentanaFotos {
  /** Posición 1-based de la primera foto del tramo (ya acotada al total). */
  desde: number;
  /** Índices 0-based dentro del lote. Vacío si no hay nada que mostrar. */
  indices: number[];
}

/** Una foto lista para el bloque. `url` en null = no se pudo subir/firmar. */
export interface FotoParaVer {
  id: string;
  nombre: string;
  url: string | null;
}

const entero = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.round(n) : null;
};

/**
 * Qué tramo pidió el modelo, acotado a lo que existe. `desde` es 1-based porque es lo que
 * el modelo lee y escribe ("las fotos 11 a 20"); los `foto_id` siguen siendo `f0`-based y
 * los bloques imprimen los dos, como hace `ver_productos` con el `product_id`.
 *
 * Un `desde` pasado del total NO se recorta al último tramo: devuelve vacío, y el bloque
 * lo dice. Recortarlo en silencio haría que el modelo crea que miró algo que no miró.
 */
export function ventanaDeFotos(total: number, entrada: unknown): VentanaFotos {
  const t = Math.max(0, Math.floor(total));
  const o = (entrada ?? {}) as { desde?: unknown; cantidad?: unknown };
  const pedido = entero(o.desde) ?? 1;
  const cantidad = Math.min(Math.max(entero(o.cantidad) ?? MAX_FOTOS_A_VER, 1), MAX_FOTOS_A_VER);
  const desde = Math.max(pedido, 1);
  if (t === 0 || desde > t) return { desde, indices: [] };
  const indices: number[] = [];
  for (let i = desde - 1; i < Math.min(t, desde - 1 + cantidad); i++) indices.push(i);
  return { desde, indices };
}

/**
 * Los bloques del `tool_result`. Una foto que no se pudo subir se nombra igual (sin
 * imagen): el modelo tiene que saber que ese `foto_id` existe y que no la vio, en vez de
 * creer que el lote tenía una foto menos.
 */
export function bloquesVerFotos(args: {
  total: number;
  desde: number;
  fotos: FotoParaVer[];
}): BloqueContenido[] {
  const { total, desde, fotos } = args;
  if (total === 0) {
    return [
      {
        type: 'text',
        text: 'El usuario no adjuntó fotos en montón. Si necesitás ver alguna, pedile que la adjunte con el "+".',
      },
    ];
  }
  if (fotos.length === 0) {
    return [
      {
        type: 'text',
        text: `Ese tramo no existe: el usuario adjuntó ${total} foto(s). Pedí un \`desde\` entre 1 y ${total}.`,
      },
    ];
  }
  const hasta = desde + fotos.length - 1;
  const bloques: BloqueContenido[] = [
    {
      type: 'text',
      text: `Fotos ${desde} a ${hasta} de las ${total} que adjuntó el usuario, en orden. Mirálas de verdad y usá lo que ves para decidir qué hacer con cada una.`,
    },
  ];
  fotos.forEach((f, i) => {
    bloques.push({
      type: 'text',
      text: `#${desde + i} foto_id=${f.id} · ${f.nombre}${f.url ? ':' : ' (no se pudo cargar)'}`,
    });
    if (f.url) bloques.push({ type: 'image', source: { type: 'url', url: f.url } });
  });
  const quedan = total - hasta;
  if (quedan > 0) {
    bloques.push({
      type: 'text',
      text: `Quedan ${quedan} foto(s) que no miraste. Si te hacen falta, volvé a llamar ver_fotos con desde=${hasta + 1}.`,
    });
  }
  return bloques;
}

/** El lote vive en memoria de la pestaña: tras una recarga no está. Se lo decimos al
 *  modelo en vez de devolverle un tramo vacío, que leería como "no había fotos". */
export const bloquesFotosPerdidas = (): BloqueContenido[] => [
  {
    type: 'text',
    text: 'Las fotos ya no están disponibles (se recargó la página desde que las adjuntó). Pedile al usuario que las vuelva a adjuntar con el "+".',
  },
];

// ── Mirar fotos puntuales, del origen que sean ─────────────────────────────
// ADR-2026-09-22-mirar-una-imagen-no-depende-de-su-tipo.
//
// Para el AGENTE mirar es UNA operación: "mostrame esa foto que no tengo a la vista". El
// id alcanza; de qué tabla o de qué almacenamiento sale lo resuelve `resolverImagenPorId`.
// Acá queda sólo el parseo de lo que pidió y el armado de los bloques. El tramo
// (`desde`/`cantidad`) sobrevive como atajo para paginar el montón cuando no se señala
// nada, que es el único caso donde listar ids sería absurdo (pueden ser 80).

import { comoSePasaComoReferencia, NOMBRE_DE_TIPO, type ImagenResuelta } from "../resolverImagenPorId";

/**
 * Los ids que pidió el modelo en `fotos`: uno o varios, saneados, sin repetir y acotados
 * al mismo tope que el tramo. NO se filtra por forma: cualquier id del chat vale y el
 * resolver dice si existe — filtrar acá volvería a meter el tipo en la interfaz.
 */
export function idsDeFotosPedidas(entrada: unknown): string[] {
  const o = (entrada ?? {}) as { fotos?: unknown };
  const crudo = o.fotos;
  const lista = Array.isArray(crudo) ? crudo : crudo === undefined || crudo === null ? [] : [crudo];
  const out: string[] = [];
  for (const v of lista) {
    const id = typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "";
    if (!id || id.length > 64 || out.includes(id)) continue;
    out.push(id);
    if (out.length >= MAX_FOTOS_A_VER) break;
  }
  return out;
}

/**
 * Los bloques cuando se miran fotos SEÑALADAS por id.
 *
 * Cada una va con qué es, su nombre si lo tiene y CÓMO se pasa después como referencia:
 * mirarla y saber usarla son la misma necesidad. Un id que no existe se nombra aparte,
 * para que el modelo no crea que miró menos de lo que pidió.
 */
export function bloquesVerImagenes(args: {
  fotos: ImagenResuelta[];
  inexistentes: string[];
}): BloqueContenido[] {
  const { fotos, inexistentes } = args;
  if (fotos.length === 0) {
    return [
      {
        type: "text",
        text: inexistentes.length
          ? `Ninguno de esos ids es una imagen de este chat (${inexistentes.join(", ")}). Los ids válidos son los de tus catálogos (producto, modelo, pose, fondo), los de lo que generaste (image_id) y los de lo que adjuntó el usuario, que dice el marcador de sus mensajes.`
          : "No pediste ninguna foto. Pasá los ids en `fotos`, o llamá sin nada para ver las que adjuntó en montón.",
      },
    ];
  }
  const bloques: BloqueContenido[] = [
    {
      type: "text",
      text: `${fotos.length === 1 ? "La imagen que pediste" : `Las ${fotos.length} imágenes que pediste`}. Mirálas de verdad: con lo que ves escribís el prompt y elegís bien, en vez de decidir por el nombre.`,
    },
  ];
  for (const f of fotos) {
    bloques.push({
      type: "text",
      text: `${NOMBRE_DE_TIPO[f.tipo]}${f.nombre ? ` "${f.nombre}"` : ""} — ${comoSePasaComoReferencia(f)}${f.url ? ":" : " (no se pudo cargar)"}`,
    });
    if (f.url) bloques.push({ type: "image", source: { type: "url", url: f.url } });
  }
  if (inexistentes.length) {
    bloques.push({
      type: "text",
      text: `No existen: ${inexistentes.join(", ")}. Esos ids no son imágenes de este chat (o el montón ya no está porque se recargó la página).`,
    });
  }
  return bloques;
}
